import { UserOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Avatar, Flex, Menu, Modal, Popover, Space, Typography } from 'antd';
import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { ROUTER_URL } from '../../router/constants';
import { useLogout, useUserSessionAPI } from '../../router/selectors';
import { TOKEN_KEY } from '../../state/api/axios';
import { FallbackComponent } from '../Fallback';
import { LogoutIcon, UserIcon } from '../Icon';

interface UserMenuProps {
  onChangeActiveRoute: (route: string[]) => void;
}
interface UserBadgeProps extends UserMenuProps {
  collapsed: boolean;
}

const StyledAvatar = styled(Avatar)`
  background: rgba(255, 255, 255, 0.2);
`;

const Container = styled(Flex)`
  position: absolute;
  bottom: 0;
  width: 100%;
  padding: 8px;
  margin-bottom: 12px;
`;

const Badge = styled(Space)`
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.1);
  width: 100%;
  padding: 8px 8px;
  cursor: pointer;
  justify-content: center;
`;

const FullName = styled.span`
  display: inline-block;
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  width: 140px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Role = styled.span`
  font-size: 12px;
  color: #d4d4d4;
  display: block;
`;

const MenuItem = styled(Menu.Item)`
  min-width: fit-content;
  .ant-menu-title-content {
    display: inline-flex;
    align-items: center;
  }
`;

const StyledMenu = styled(Menu)`
  &.ant-menu-light.ant-menu-root.ant-menu-vertical {
    border-inline-end: unset;
  }
`;

const UserMenu: FC<UserMenuProps> = ({ onChangeActiveRoute }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const [logoutData, logout, clearLogoutData] = useLogout();

  useEffect(() => {
    if (logoutData.data) {
      localStorage.removeItem(TOKEN_KEY);
      navigate('/auth/login', { replace: true });
      return clearLogoutData();
    }
  }, [clearLogoutData, logoutData, navigate]);

  const handleLogout = () => {
    setIsModalOpen(false);
    logout();
  };

  const handleViewProfile = () => {
    navigate(ROUTER_URL.UserProfile);
    onChangeActiveRoute(['']);
  };

  return (
    <FallbackComponent overlay requesting={!!logoutData.requesting}>
      <div className="w-44">
        <StyledMenu selectedKeys={[]}>
          <MenuItem onClick={handleViewProfile} key="profile">
            <Flex align="center" justify="center">
              <UserIcon color="#737373" />
              <div className="pl-2">View Profile</div>
            </Flex>
          </MenuItem>
          <MenuItem onClick={() => setIsModalOpen(true)} key="logout">
            <Flex align="center" justify="center">
              <LogoutIcon />
              <div className="pl-2">Logout</div>
            </Flex>
          </MenuItem>
        </StyledMenu>
        <Modal
          title="Confirm logout"
          okText="Confirm"
          centered
          open={isModalOpen}
          onOk={handleLogout}
          cancelButtonProps={{ size: 'large' }}
          okButtonProps={{ size: 'large' }}
          onCancel={() => setIsModalOpen(false)}
        >
          <Typography.Text className="mt-2 mb-3 inline-block">
            Are you sure you want to logout?
          </Typography.Text>
        </Modal>
      </div>
    </FallbackComponent>
  );
};

export const UserBadge: FC<UserBadgeProps> = ({
  collapsed,
  onChangeActiveRoute,
}) => {
  const [userSessionData] = useUserSessionAPI();
  return (
    <Container>
      <Popover
        content={<UserMenu onChangeActiveRoute={onChangeActiveRoute} />}
        trigger="click"
      >
        <Badge>
          <Flex>
            <StyledAvatar
              src={userSessionData.data?.avatar}
              size={36}
              icon={<UserOutlined />}
            />
          </Flex>
          {!collapsed && (
            <div>
              <FullName>
                {userSessionData.data?.firstName}{' '}
                {userSessionData.data?.lastName}
              </FullName>
              <Role>{userSessionData.data?.role.name}</Role>
            </div>
          )}
        </Badge>
      </Popover>
    </Container>
  );
};
